import api from './client'
import type { PromptInputMode } from './projectVariables'
import type { ShellPrefsDoc, ShellPrefsTransport } from '@twasta/page-runtime/shell'

/** One option for a `select`-mode prompt variable (col1=value, col2=label). */
export interface PromptOption {
  value: string
  label: string
}

/** A prompt variable described for the login prompt / Project settings. */
export interface PromptVariable {
  name: string
  prompt: string
  input_mode: PromptInputMode
  required: boolean
  external: boolean
  remember_default: boolean
  default: string
  description: string
  value: string | null
  remember: boolean
  pending: boolean
  options: PromptOption[]
  /** True when a select variable's source/master table can't be read yet
   *  (e.g. not created on a fresh import) — the UI tells the user the master
   *  isn't defined and lets them type a value. */
  source_missing?: boolean
  /** The app's security context ("Use in rights model") — see PromptModal. */
  security_context?: boolean
}

/** The security-context block the Run identity endpoints return. */
export interface SecurityContextState {
  var: string
  label: string
  value: string | null
  /** True when the user has no usable value — the app must ask first. */
  required: boolean
}

/**
 * Rights follow the security context (e.g. financial entity): after it
 * changes, everything on screen was loaded under the previous entity's roles.
 * Reload the running application so the menu, buttons and open screens all
 * come back under the new one — one well-defined moment instead of a page
 * where some parts know about the switch and some do not.
 */
export function notifySecurityContextChanged(): void {
  try { window.location.reload() } catch { /* not in a browser */ }
}

export interface PromptValueIn {
  var_name: string
  value: string
  remember: boolean
}

export type MemorySurface = 'internal' | 'site' | 'external'

/**
 * Session start — clears the user's non-remembered values and returns the
 * prompt variables that still need a value. Call once per browser session.
 */
export async function bootstrapUserMemory(
  projectId: string, surface: MemorySurface = 'internal',
): Promise<PromptVariable[]> {
  const res = await api.get(
    `/user-memory/${encodeURIComponent(projectId)}/bootstrap`,
    { params: { surface } },
  )
  return (res.data?.pending || []) as PromptVariable[]
}

/** All prompt variables with the user's current value (Project settings). */
export async function listUserMemory(
  projectId: string, surface: MemorySurface = 'internal',
): Promise<PromptVariable[]> {
  const res = await api.get(
    `/user-memory/${encodeURIComponent(projectId)}`,
    { params: { surface } },
  )
  return (res.data?.variables || []) as PromptVariable[]
}

/** Persist the user's prompt-variable values (with remember flags). */
export async function saveUserMemory(
  projectId: string, values: PromptValueIn[],
): Promise<void> {
  await api.post(`/user-memory/${encodeURIComponent(projectId)}`, { values })
}

/** De-select remember for one variable — it is re-asked next session. */
export async function forgetUserMemory(
  projectId: string, varName: string,
): Promise<void> {
  await api.post(`/user-memory/${encodeURIComponent(projectId)}/forget`, {
    var_name: varName,
  })
}

// ── Run shell preferences (layout / Pinned / Recent) ───────────────────────
// The user-memory store's `__shell__` record — per user, per application,
// for both kinds of user (the run-session headers ride every call).


export function makeShellPrefsTransport(projectId: string): ShellPrefsTransport {
  const url = `/user-memory/${encodeURIComponent(projectId)}/shell`
  return {
    load: async () => {
      const res = await api.get(url)
      return (res.data?.prefs ?? null) as Partial<ShellPrefsDoc> | null
    },
    save: async (doc: ShellPrefsDoc) => {
      await api.put(url, { prefs: doc })
    },
    // The desktop picture: its own record, so the prefs document stays small.
    loadWallpaper: async () => {
      const res = await api.get(`${url}/wallpaper`)
      return (res.data?.image as string | null | undefined) ?? null
    },
    saveWallpaper: async (image: string | null) => {
      if (image) await api.put(`${url}/wallpaper`, { image })
      else await api.delete(`${url}/wallpaper`)
    },
  }
}
